import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { DocumentModel } from 'core/domain/documents/model/documentsEntityModels';
import { GetDocumentListByAssetId } from 'core/domain/documents/repositories/getDocumentListByAssetId';
import { CreateDocumentsByAssetId } from 'core/domain/documents/repositories/createDocumentByAssetId';
import { DocumentManagementFormValues } from 'components/organisms/DocumentManagementForm';
import { GetDocumentListByProjectId } from 'core/domain/documents/repositories/getDocumentListByProjectId';
import { CreateDocumentsByProjectId } from 'core/domain/documents/repositories/createDocumentByProjectId';
import { DeleteDocument } from 'core/domain/documents/repositories/deleteDocument';
import { TableActionsMenuProps } from 'components/atoms/TableAtom';
import { getUrlBaseRoute } from 'utils/url';
import { formatBytes } from 'utils/documents';
import { SourceType } from 'components/organisms/DocumentsTable/DocumentsTableActionsManager';
import { DocumentsColumns } from 'components/organisms/DocumentsTable/DocumentsColumns';
import { TABLE_PAGINATION_PAGE_DEFAULT, TABLE_PAGINATION_SIZE_DEFAULT } from 'constants/table';

enum DocumentRouterType {
  PROJECTS = 'projects',
  ASSETS = 'assets',
}

const DEFAULT_TOTAL_PAGES = 1;

export const useDocumentList = () => {
  const { projectId, assetId } = useParams<{ projectId: string; assetId: string }>();
  const history = useHistory();
  const { t } = useTranslation();
  const [data, setData] = useState<DocumentModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleteDocumentModalVisible, setIsDeleteDocumentModalVisible] = useState<boolean>(false);
  const [currentDocumentId, setCurrentDocumentId] = useState<string>('');

  const pageCurrent = Number(TABLE_PAGINATION_PAGE_DEFAULT);
  const pageSize = Number(TABLE_PAGINATION_SIZE_DEFAULT);

  const router = getUrlBaseRoute(history.location.pathname) as DocumentRouterType;
  const isProjectsRoute = router === DocumentRouterType.PROJECTS;
  const source = isProjectsRoute ? SourceType.PROJECT : SourceType.ASSET;

  const wrongSourceText = t('view_in_projects');
  const deleteModalTitle = t('_ASSET_DOCUMENTS_DELETE_MODAL_TITLE');
  const deleteModalText = t('DOCUMENT_MANAGEMENT.delete_document_confirm');
  const deleteModalMainButtonText = t('delete');
  const deleteModalSecondaryButtonText = t('cancel');

  const documentTableActionsMenu: TableActionsMenuProps[] = [
    {
      key: 'deleteDocument',
      onClick: (documentId: string) => onDeleteDocument(documentId),
      text: t('delete'),
      disabled: false,
    },
  ];

  const onGetDocumentsList = async () => {
    setIsLoading(true);
    try {
      const data = isProjectsRoute ? await GetDocumentListByProjectId(projectId) : await GetDocumentListByAssetId(assetId);
      data && setData(data);
    } catch (error) {
      console.warn(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async (): Promise<void> => {
    onGetDocumentsList();
  };

  const onSave = async (values: DocumentManagementFormValues) => {
    const { files } = values;
    setIsLoading(true);
    try {
      const response = isProjectsRoute
        ? await CreateDocumentsByProjectId({ projectId, files })
        : await CreateDocumentsByAssetId({ assetId, files });
      await onRefresh();
      return response;
    } catch (error) {
      console.warn(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteDocument = (documentId: string) => {
    setCurrentDocumentId(documentId);
    setIsDeleteDocumentModalVisible(true);
  };

  const onFormatToBytes = (document: { bytes: number; decimals?: number }): string => {
    return formatBytes(document);
  };

  const onConfirmDeleteDocument = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await DeleteDocument(currentDocumentId);
      message.success(t('deleted_document'), 3);
      await onRefresh();
    } catch (error) {
      console.warn(error);
    } finally {
      setIsLoading(false);
      setIsDeleteDocumentModalVisible(false);
    }
  };

  const onCloseConfirmModal = () => {
    setIsDeleteDocumentModalVisible(false);
  };

  const onRowClick = ({ url }: DocumentModel) => {
    window.open(url, '_blank', 'noreferrer');
  };

  const onPagination = (_page: number, _size?: number) => {};

  const columns = DocumentsColumns({
    source,
    wrongSourceText,
    onFormat: onFormatToBytes,
    documentTableActionsMenu,
  });

  useEffect(() => {
    onGetDocumentsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    columns,
    deleteModalTitle,
    deleteModalText,
    deleteModalMainButtonText,
    deleteModalSecondaryButtonText,
    pageCurrent,
    pageSize,
    totalPages: DEFAULT_TOTAL_PAGES,
    onSave,
    onRefresh,
    onFormatToBytes,
    onRowClick,
    onCloseConfirmModal,
    onConfirmDeleteDocument,
    onPagination,
    loading: isLoading,
    deleteModalVisible: isDeleteDocumentModalVisible,
  };
};
